import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { ConversionFeature } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ScheduleDemoButtonLocation } from "@redotech/redo-merchant-conversion-rpc/schema/analytics/send-schedule-demo-clicked-notification";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import { useConversionLockedAction } from "../../../../conversion/locked/use-conversion-locked-action";

interface AnalyticsChildLock {
  locked: boolean;
  shouldHide: boolean;
  onLockedClick: () => void | Promise<void>;
}

function getFirstUnlockedAnalyticsHref({
  checkout,
  postPurchase,
  customReports,
  canViewReports,
  checkoutHref,
  postPurchaseHref,
  reportsHref,
}: {
  checkout: AnalyticsChildLock;
  postPurchase: AnalyticsChildLock;
  customReports: AnalyticsChildLock;
  canViewReports: boolean;
  checkoutHref: string;
  postPurchaseHref: string;
  reportsHref: string;
}): string {
  if (!checkout.locked) {
    return checkoutHref;
  }
  if (!postPurchase.locked) {
    return postPurchaseHref;
  }
  if (canViewReports && !customReports.locked) {
    return reportsHref;
  }
  return checkoutHref;
}

function getCollapsedLockedClick({
  checkout,
  postPurchase,
  customReports,
}: {
  checkout: AnalyticsChildLock;
  postPurchase: AnalyticsChildLock;
  customReports: AnalyticsChildLock;
}): () => void | Promise<void> {
  if (!checkout.shouldHide && checkout.locked) {
    return checkout.onLockedClick;
  }
  if (!postPurchase.shouldHide && postPurchase.locked) {
    return postPurchase.onLockedClick;
  }
  return customReports.onLockedClick;
}

export const AnalyticsItems = memo(function AnalyticsItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const checkoutLock = useConversionLockedAction(
    ScheduleDemoButtonLocation.NAV_ANALYTICS_CHECKOUT,
    ConversionFeature.ANALYTICS_CHECKOUT,
  );
  const postPurchaseLock = useConversionLockedAction(
    ScheduleDemoButtonLocation.NAV_ANALYTICS_POST_PURCHASE,
    ConversionFeature.ANALYTICS_POST_PURCHASE,
  );
  const customReportsLock = useConversionLockedAction(
    ScheduleDemoButtonLocation.NAV_ANALYTICS_CUSTOM_REPORTS,
    ConversionFeature.ANALYTICS_CUSTOM_REPORTS,
  );

  const canViewReports = useHasPermission(
    OrderManagementPermissions.VIEW_REPORTS,
  );
  const conversionAnalyticsRoot = routes.conversionAnalyticsHref;
  const anyChildSelected = useMatch(`${conversionAnalyticsRoot}/*`);

  const checkoutHref = routes.getConversionSubRoute("analytics/checkout");
  const postPurchaseHref = routes.getConversionSubRoute(
    "analytics/post-purchase",
  );
  const reportsHref = routes.conversionAnalyticsReportsHref;

  const reportsHidden = !canViewReports || customReportsLock.shouldHide;
  const allChildrenHidden =
    checkoutLock.shouldHide && postPurchaseLock.shouldHide && reportsHidden;

  if (allChildrenHidden) {
    return null;
  }

  const firstUnlockedHref = getFirstUnlockedAnalyticsHref({
    checkout: checkoutLock,
    postPurchase: postPurchaseLock,
    customReports: customReportsLock,
    canViewReports,
    checkoutHref,
    postPurchaseHref,
    reportsHref,
  });

  const allVisibleLocked =
    (checkoutLock.shouldHide || checkoutLock.locked) &&
    (postPurchaseLock.shouldHide || postPurchaseLock.locked) &&
    (reportsHidden || customReportsLock.locked);

  // Every visible child is locked → collapse to a single locked parent row
  // (same treatment as other top-level locked surfaces).
  if (allVisibleLocked) {
    return (
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: getCollapsedLockedClick({
            checkout: checkoutLock,
            postPurchase: postPurchaseLock,
            customReports: customReportsLock,
          }),
        }}
        dimmed
        FilledIcon={BarChart10FilledIcon}
        friendlyName="Analytics"
        id="analytics"
        isPrivate
        UnfilledIcon={BarChart10Icon}
      />
    );
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: firstUnlockedHref }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName="Analytics"
      id="analytics"
      UnfilledIcon={BarChart10Icon}
    >
      {!checkoutLock.shouldHide && (
        <RedoSecondaryNavItem
          action={
            checkoutLock.locked
              ? { type: "button", onClick: checkoutLock.onLockedClick }
              : { type: "link", href: checkoutHref }
          }
          dimmed={checkoutLock.locked}
          friendlyName="Checkout"
          id="checkout-analytics"
          isPrivate={checkoutLock.locked}
        />
      )}
      {!postPurchaseLock.shouldHide && (
        <RedoSecondaryNavItem
          action={
            postPurchaseLock.locked
              ? { type: "button", onClick: postPurchaseLock.onLockedClick }
              : { type: "link", href: postPurchaseHref }
          }
          dimmed={postPurchaseLock.locked}
          friendlyName="Post-purchase"
          id="post-purchase-analytics"
          isPrivate={postPurchaseLock.locked}
        />
      )}
      {canViewReports && !customReportsLock.shouldHide && (
        <RedoSecondaryNavItem
          action={
            customReportsLock.locked
              ? { type: "button", onClick: customReportsLock.onLockedClick }
              : { type: "link", href: reportsHref }
          }
          beta
          dimmed={customReportsLock.locked}
          friendlyName="Custom reports"
          id="conversion-analytics-custom-reports"
          isPrivate={customReportsLock.locked}
        />
      )}
    </RedoSecondaryNavItem>
  );
});
